import { Link, useSearchParams } from 'react-router-dom';
import { Package } from 'lucide-react';
import { useLoPublicSiteTranslation } from '@/hooks/useLoPublicSiteTranslation';
import type { LoPublicPhysicalProductCard } from '@/lib/lyrraone-public';
import { LoFormationCardGrid } from '@/components/lyrraone/formation-cards';
import { publicSiteLinkLang } from '@/lib/lo-public-site-locale';
import { creatorPhysicalProductHref } from '@/lib/lyrraone-site-links';
import { usePublicRegionStore } from '@/stores/public-region.store';
import { formatLyrraOnePrice } from '@/lib/store-formation';
import { imagesApi } from '@/api/images.api';
import { formationCardGradient } from '@/components/lyrraone/formation-cards/utils';

interface Props {
  products: LoPublicPhysicalProductCard[];
  siteSlug: string;
  customDomainSlug?: string | null;
  emptyMessage?: string;
}

export function LoPublicProductGrid({
  products,
  siteSlug,
  customDomainSlug,
  emptyMessage,
}: Props) {
  const { t } = useLoPublicSiteTranslation();
  const region = usePublicRegionStore((s) => s.region);
  const [searchParams] = useSearchParams();
  const linkLang = publicSiteLinkLang(searchParams.get('lang'));
  const resolvedEmpty = emptyMessage ?? (t('boutique.empty') as string);

  if (products.length === 0) {
    return <p className="text-center text-gray-500">{resolvedEmpty}</p>;
  }

  return (
    <LoFormationCardGrid>
      {products.map((product) => {
        const href = creatorPhysicalProductHref(product, siteSlug, customDomainSlug, linkLang);
        const priceLabel = formatLyrraOnePrice(product.price, region);
        const imageUrl = product.imageUrl ? imagesApi.resolveUrl(product.imageUrl) : null;
        const outOfStock = product.stock < 1;

        return (
          <Link key={product.id} to={href} className="lo-fc-market">
            <div className="lo-fc-market__visual">
              {outOfStock ? (
                <span className="lo-fc-market__featured">{t('boutique.outOfStock') as string}</span>
              ) : null}
              {imageUrl ? (
                <img src={imageUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
              ) : (
                <div
                  className="lo-fc-market__gradient flex h-full w-full items-center justify-center"
                  style={{ background: formationCardGradient(product.id) }}
                >
                  <Package className="h-10 w-10 text-white/80" aria-hidden />
                </div>
              )}
            </div>
            <div className="lo-fc-market__body">
              <h3 className="lo-fc-market__title">{product.name}</h3>
              {product.description?.trim() ? (
                <p className="lo-fc-market__desc line-clamp-2">{product.description}</p>
              ) : null}
              <p className="lo-fc-market__price">{priceLabel}</p>
            </div>
          </Link>
        );
      })}
    </LoFormationCardGrid>
  );
}
